import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, FileClock, FileDown, FileSpreadsheet, Loader2, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ALL = '__all__';
const PERIODOS = [['tudo', 'Desde o início'], ['hoje', 'Hoje'], ['7d', '7 dias'], ['30d', '30 dias'], ['personalizado', 'Personalizado']];
const ACT_TONE = { login: 'bg-sky-100 text-sky-800', logout: 'bg-slate-100 text-slate-700', 'criação': 'bg-emerald-100 text-emerald-800', 'edição': 'bg-amber-100 text-amber-800', 'exclusão': 'bg-red-100 text-red-800' };
const fmt = (v) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' }) : '—');
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== ALL));

function Sel({ value, onChange, items, placeholder, testid, className = 'w-48', noAll }) {
  return (
    <Select value={value || ALL} onValueChange={onChange}>
      <SelectTrigger className={className} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-80">{(noAll ? items : [[ALL, placeholder], ...items]).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Json({ title, v }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
      <pre className="max-h-72 overflow-auto rounded-lg bg-slate-50 p-2 text-[11px] text-slate-700" data-testid={`log-detalhe-${title.toLowerCase()}`}>{v ? JSON.stringify(v, null, 2) : 'não disponível'}</pre>
    </div>
  );
}

function Detalhe({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (id) api.get(`/auditoria/eventos/${id}`).then(({ data }) => setD(data)).catch(() => toast.error('Falha ao carregar o evento.')); else setD(null); }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="log-detalhe">
        <DialogHeader><DialogTitle>Detalhes do evento</DialogTitle><DialogDescription>{d ? `${fmt(d.at)} · ${d.user_name || d.user_email || 'sistema'}` : 'Carregando…'}</DialogDescription></DialogHeader>
        {d && (
          <div className="space-y-3 text-[12.5px]">
            <div className="grid gap-2 sm:grid-cols-2">
              {[['Módulo', d.module], ['Ação', d.action], ['Tipo', d.event_type], ['Registro afetado', `${d.entity || ''} ${d.entity_id || ''}`.trim() || '—'], ['IP/Origem', d.ip_address || '—'], ['Rota', d.origin ? `${d.origin.method} ${d.origin.path}` : '—'], ['Status', d.status_code || '—'], ['Navegador', d.origin?.user_agent || '—']].map(([k, v]) => <p key={k} className="break-all"><span className="text-slate-400">{k}: </span><b className="text-slate-800">{String(v)}</b></p>)}
            </div>
            <p className="text-slate-700">{d.description}</p>
            <div className="flex flex-col gap-3 md:flex-row"><Json title="Antes" v={d.before} /><Json title="Depois" v={d.after} /></div>
            {d.metadata && <Json title="Metadados" v={d.metadata} />}
            <p className={`flex items-center gap-1 text-[12px] font-bold ${d.integridade === 'íntegro' ? 'text-emerald-700' : d.integridade === 'ALTERADO' ? 'text-red-700' : 'text-slate-500'}`} data-testid="log-integridade"><ShieldCheck className="h-4 w-4" /> Integridade: {d.integridade}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Filtros({ meta, f, set }) {
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-slate-100 bg-white p-3" data-testid="log-filtros">
      {meta.pode_filtrar_todos && <Sel value={f.user_id} onChange={(v) => set('user_id', v)} placeholder="Todos os usuários" testid="log-filtro-usuario" className="w-64" items={meta.usuarios.map((u) => [u.id, `${u.name || u.email} · ${u.role || ''}`])} />}
      <Sel value={f.periodo} onChange={(v) => set('periodo', v)} placeholder="Período" testid="log-filtro-periodo" className="w-44" items={PERIODOS} noAll />
      {f.periodo === 'personalizado' && <><Input type="date" className="w-40" value={f.inicio || ''} onChange={(e) => set('inicio', e.target.value)} data-testid="log-filtro-inicio" /><Input type="date" className="w-40" value={f.fim || ''} onChange={(e) => set('fim', e.target.value)} data-testid="log-filtro-fim" /></>}
      <Sel value={f.modulo} onChange={(v) => set('modulo', v)} placeholder="Todos os módulos" testid="log-filtro-modulo" items={meta.modulos.map((m) => [m, m])} />
      <Sel value={f.acao} onChange={(v) => set('acao', v)} placeholder="Todas as ações" testid="log-filtro-acao" className="w-40" items={meta.acoes.map((a) => [a, a])} />
      <div className="relative min-w-[220px] flex-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input className="pl-9" placeholder="Buscar por descrição, registro, e-mail…" value={f.q || ''} onChange={(e) => set('q', e.target.value)} data-testid="log-filtro-busca" /></div>
    </div>
  );
}

function Linha({ r, onOpen }) {
  return (
    <tr className="hover:bg-slate-50" data-testid={`log-row-${r.id}`}>
      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">{fmt(r.at)}</td>
      <td className="px-3 py-2"><b className="text-slate-800">{r.user_name || '—'}</b><span className="block text-[11px] text-slate-400">{r.user_email || (r.user_id ? r.user_id.slice(0, 8) : 'sistema')}</span></td>
      <td className="px-3 py-2">{r.module}</td>
      <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${ACT_TONE[r.action.split(' ')[0]] || 'bg-violet-100 text-violet-800'}`}>{r.action}</span></td>
      <td className="max-w-[360px] px-3 py-2"><span className="line-clamp-2 text-slate-700">{r.description}</span>{r.entity_id && <span className="block font-mono text-[10.5px] text-slate-400">{r.entity} {r.entity_id}</span>}</td>
      <td className="px-3 py-2 text-[11.5px] text-slate-500">{r.ip_address || '—'}{r.source === 'requisicao' && <Badge variant="outline" className="ml-1 text-[9.5px]">req.</Badge>}</td>
      <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => onOpen(r.id)} data-testid={`log-ver-${r.id}`}><Eye className="h-4 w-4" /> Ver detalhes</Button></td>
    </tr>
  );
}

async function baixar(kind, params) {
  try {
    const { data } = await api.get(`/auditoria/exportar.${kind}`, { params, responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(data); a.download = `auditoria.${kind}`; a.click();
  } catch { toast.error('Falha ao exportar.'); }
}

export default function Logs() {
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ periodo: 'tudo' });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [ordem, setOrdem] = useState('desc');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get('/auditoria/filtros').then(({ data: d }) => setMeta(d)).catch(() => toast.error('Erro ao carregar filtros')); }, []);
  useEffect(() => { const t = setTimeout(() => setQ(f.q || ''), 400); return () => clearTimeout(t); }, [f.q]);
  const params = clean({ ...f, q });
  const key = JSON.stringify(params);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get('/auditoria/eventos', { params: { ...JSON.parse(key), page, page_size: 50, ordem } }); setData(d); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao carregar logs'); } finally { setLoading(false); }
  }, [key, page, ordem]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v === ALL ? undefined : v })); setPage(1); if (k === 'user_id') setOrdem(v && v !== ALL ? 'asc' : 'desc'); };
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="logs-page">
      <div className="flex flex-wrap items-center gap-3">
        <FileClock className="h-7 w-7 text-[#7B3F98]" />
        <div className="flex-1"><h1 className="text-2xl font-extrabold text-slate-900">Log de Auditoria</h1><p className="text-[13px] text-slate-500">Tudo o que cada usuário fez, desde o primeiro acesso. Registros imutáveis com selo de integridade.</p></div>
        <Button variant="outline" onClick={() => baixar('xlsx', params)} data-testid="log-exportar-excel"><FileSpreadsheet className="h-4 w-4" /> Exportar Excel</Button>
        <Button variant="outline" onClick={() => baixar('pdf', params)} data-testid="log-exportar-pdf"><FileDown className="h-4 w-4" /> Exportar PDF</Button>
      </div>
      {meta && <Filtros meta={meta} f={f} set={set} />}
      <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-slate-500">
        <span data-testid="log-total">{data ? `${data.total.toLocaleString('pt-BR')} evento(s)` : '—'}</span>
        {data?.primeiro_registro && <span>· primeiro registro: <b>{fmt(data.primeiro_registro)}</b></span>}
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={() => { setOrdem(ordem === 'asc' ? 'desc' : 'asc'); setPage(1); }} data-testid="log-ordem">{ordem === 'asc' ? 'Mais antigos primeiro' : 'Mais recentes primeiro'}</Button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white">
        <table className="w-full min-w-[1000px] text-[12.5px]" data-testid="log-tabela">
          <thead><tr className="text-left text-[10.5px] font-bold uppercase tracking-wider text-slate-400">{['Data/Hora', 'Usuário', 'Módulo', 'Ação', 'Registro / descrição', 'IP/Origem', ''].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading && !data && <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
            {data?.items.map((r) => <Linha key={r.id} r={r} onOpen={setOpen} />)}
            {data && !data.items.length && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Nenhum evento para estes filtros.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && (
        <div className="flex items-center justify-end gap-2 text-[12.5px]" data-testid="log-paginacao">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="log-pagina-anterior"><ChevronLeft className="h-4 w-4" /></Button>
          <span>Página {data.page} de {data.pages}</span>
          <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="log-pagina-proxima"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
      <Detalhe id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
